import {
  DeleteOutlined,
  DownloadOutlined,
  EyeOutlined,
  FileDoneOutlined,
  PlusOutlined,
  ReloadOutlined,
  SaveOutlined,
  SendOutlined,
  UploadOutlined,
} from '@ant-design/icons';
import { ControllerResponse } from '@/DTOS/Controller/ControllerResponse';
import { UoReport, UoReportSearch, UoReportType } from '@/DTOS/UoReport/UoReport';
import SignaturePad from '@/components/SignaturePad/SignaturePad';
import { useAuth } from '@/context/AuthProvider';
import { saveBlobCrossPlatform } from '@/utils/saveBlobCrossPlatform';
import { App, Button, DatePicker, Divider, Empty, Input, Modal, Popconfirm, Select, Space, Spin, Table, Tag, Upload, Typography } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import type { UploadFile, RcFile } from 'antd/es/upload/interface';
import dayjs, { Dayjs } from 'dayjs';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import UoReportService from '../../../services/uoReports/UoReportService';
import {
  cloneWordBlocks,
  UO_DOCUMENT_TEMPLATES,
  UO_TEMPLATE_BY_KEY,
  UoDocumentTemplate,
  UoWordBlock,
  UoWordTableCell,
  UoWordTableRow,
  UoWordTable,
} from './uoDocumentTemplates';
import './UORapoarte.css';

const { TextArea } = Input;
const { Text } = Typography;
const DATE_FORMAT = 'DD.MM.YYYY';

const STATUS_LABELS: Record<string, { label: string; color: string }> = {
  draft: { label: 'Draft', color: 'orange' },
  signed: { label: 'Semnat', color: 'blue' },
  sent: { label: 'Trimis', color: 'green' },
};

const initialTemplateKey: UoReportType = 'visit';
const WORD_LIKE_TEMPLATE_KEYS = new Set<UoReportType>([
  'visit',
  'monthly',
  'physicalInspection',
  'upsTest',
  'configuration',
]);
const STATIC_DOCUMENT_TEMPLATE_KEYS = WORD_LIKE_TEMPLATE_KEYS;
const SIGNATURE_LABEL = 'Semnătura:';

const getFilenameFromResponse = (response: Response, fallback: string) => {
  const disposition = response.headers.get('Content-Disposition');
  const utf8Match = disposition?.match(/filename\*=UTF-8''([^;]+)/i);
  if (utf8Match?.[1]) {
    return decodeURIComponent(utf8Match[1]);
  }

  const asciiMatch = disposition?.match(/filename="?([^"]+)"?/i);
  return asciiMatch?.[1] || fallback;
};

// Export errors come back as a JSON ControllerResponse with HTTP 200, not as a file.
const readExportBlob = async (response: Response) => {
  if (response.headers.get('Content-Type')?.includes('application/json')) {
    const body = (await response.json()) as ControllerResponse<string>;
    throw new Error(body?.errors?.[0] || 'Exportul nu a putut fi generat.');
  }

  return response.blob();
};

const statusTag = (status?: string) => {
  const meta = STATUS_LABELS[status || 'draft'] || STATUS_LABELS.draft;
  return <Tag color={meta.color}>{meta.label}</Tag>;
};

const fileToDataUrl = (file: RcFile) =>
  new Promise<string>((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result || ''));
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });

const buildSearchPayload = (template: UoDocumentTemplate, monthDate: Dayjs, date: Dayjs, quarter: number): UoReportSearch => {
  if (template.periodMode === 'quarter') {
    return { type: template.key, quarter, year: monthDate.year(), skip: 0, take: 100 };
  }

  if (template.periodMode === 'month') {
    return { type: template.key, month: monthDate.month() + 1, year: monthDate.year(), skip: 0, take: 100 };
  }

  if (template.periodMode === 'date') {
    return { type: template.key, month: date.month() + 1, year: date.year(), skip: 0, take: 100 };
  }

  return { type: template.key, skip: 0, take: 100 };
};

const buildPeriodLabel = (report: UoReport) => {
  if (report.quarter && report.year) {
    return `T${report.quarter} ${report.year}`;
  }

  if (report.month && report.year) {
    return `${String(report.month).padStart(2, '0')}.${report.year}`;
  }

  return report.reportDate ? dayjs(report.reportDate).format('DD.MM.YYYY') : '-';
};

const normalize = (value?: string) =>
  (value || '')
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '');

type DocumentSignatureRole = 'technician' | 'beneficiary';
type SelectedTableCell = { blockIndex: number; rowIndex: number; cellIndex: number };

const isStaticDocumentTemplate = (templateKey: UoReportType) => STATIC_DOCUMENT_TEMPLATE_KEYS.has(templateKey);

const createBlockId = (prefix: string) => `${prefix}-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;

const getSignatureValue = (cell: UoWordTableCell) =>
  cell.signatureValue || (cell.text?.startsWith('data:image') ? cell.text : '');

const getSignatureRoleFromHeader = (header: string, cellIndex: number): DocumentSignatureRole => {
  const normalizedHeader = normalize(header);
  if (normalizedHeader.includes('beneficiar') || normalizedHeader.includes('reprezentant')) {
    return 'beneficiary';
  }

  if (normalizedHeader.includes('prestator') || normalizedHeader.includes('tehnician')) {
    return 'technician';
  }

  return cellIndex === 1 ? 'beneficiary' : 'technician';
};

const markSignatureCell = (
  cell: UoWordTableCell,
  role: DocumentSignatureRole,
  lockedLabel?: string,
): UoWordTableCell => ({
  ...cell,
  text: lockedLabel || (cell.text?.startsWith('data:image') ? '' : cell.text || ''),
  editor: 'signature',
  signatureRole: role,
  signatureValue: getSignatureValue(cell),
  lockedLabel,
});

const annotateSignatureCells = (templateKey: UoReportType, blocks: UoWordBlock[]): UoWordBlock[] => {
  if (!isStaticDocumentTemplate(templateKey)) {
    return blocks;
  }

  return blocks.map((block) => {
    if (block.type !== 'table') {
      return block;
    }

    if (templateKey === 'visit') {
      const signatureColumnIndex = block.rows[0]?.cells.findIndex((cell) => {
        const text = normalize(cell.text);
        return text.includes('semnatura') && text.includes('tehnician');
      });

      if (signatureColumnIndex === undefined || signatureColumnIndex < 0) {
        return block;
      }

      return {
        ...block,
        rows: block.rows.map((row, rowIndex) => ({
          ...row,
          cells: row.cells.map((cell, cellIndex) =>
            rowIndex > 0 && cellIndex === signatureColumnIndex ? markSignatureCell(cell, 'technician') : cell
          ),
        })),
      };
    }

    return {
      ...block,
      rows: block.rows.map((row, rowIndex) => ({
        ...row,
        cells: row.cells.map((cell, cellIndex) => {
          const normalizedCell = normalize(cell.text);
          const tableHeaderText = normalize((block.rows[0]?.cells || []).map((item) => item.text).join(' '));
          const previousRowText = normalize((block.rows[rowIndex - 1]?.cells || []).map((item) => item.text).join(' '));
          const nextRowText = normalize((block.rows[rowIndex + 1]?.cells || []).map((item) => item.text).join(' '));
          const isLegacySignatureImage =
            cell.text?.startsWith('data:image') &&
            (tableHeaderText.includes('prestator') || tableHeaderText.includes('beneficiar') || tableHeaderText.includes('tehnician')) &&
            (previousRowText.includes('nume') || nextRowText.includes('data'));

          if (!normalizedCell.startsWith('semnatura') && cell.editor !== 'signature' && !isLegacySignatureImage) {
            return cell;
          }

          const header = block.rows[0]?.cells[cellIndex]?.text || '';
          return markSignatureCell(cell, getSignatureRoleFromHeader(header, cellIndex), SIGNATURE_LABEL);
        }),
      })),
    };
  });
};

const hasInlineDocumentSignatureFields = (blocks: UoWordBlock[]) =>
  blocks.some((block) => block.type === 'table' && block.rows.some((row) => row.cells.some((cell) => cell.editor === 'signature')));

const hydrateInlineDocumentSignatures = (blocks: UoWordBlock[], technicianSignature?: string, beneficiarySignature?: string): UoWordBlock[] =>
  blocks.map((block) => {
    if (block.type !== 'table') {
      return block;
    }

    return {
      ...block,
      rows: block.rows.map((row) => ({
        ...row,
        cells: row.cells.map((cell) => {
          if (cell.editor !== 'signature' || getSignatureValue(cell) || !cell.lockedLabel) {
            return cell;
          }

          const signature = cell.signatureRole === 'beneficiary' ? beneficiarySignature : technicianSignature;
          return signature ? { ...cell, signatureValue: signature } : cell;
        }),
      })),
    };
  });

const extractInlineDocumentSignatures = (blocks: UoWordBlock[]) =>
  blocks.reduce(
    (acc, block) => {
      if (block.type !== 'table') {
        return acc;
      }

      block.rows.forEach((row) => {
        row.cells.forEach((cell) => {
          if (cell.editor !== 'signature') {
            return;
          }

          const signature = getSignatureValue(cell);
          if (!signature) {
            return;
          }

          if (cell.signatureRole === 'beneficiary' && !acc.beneficiary) {
            acc.beneficiary = signature;
          } else if (!acc.technician) {
            acc.technician = signature;
          }
        });
      });

      return acc;
    },
    { technician: '', beneficiary: '' } as Record<DocumentSignatureRole, string>,
  );

const isQuestionOrChoiceRow = (rowHeader: string) => {
  const normalized = normalize(rowHeader);
  return (
    rowHeader.includes('?') ||
    normalized.includes('identificate') ||
    normalized.includes('rezolvate') ||
    normalized.includes('verificata') ||
    normalized.includes('modificari') ||
    normalized.includes('confirmata') ||
    normalized.includes('fotografii') ||
    normalized.includes('test') ||
    normalized.includes('rezultat')
  );
};

const getRowCells = (row: any): UoWordTableCell[] => {
  if (Array.isArray(row)) {
    return row.map((cell, cellIndex) =>
      typeof cell === 'string'
        ? {
            id: `legacy-${cellIndex}`,
            text: cell,
            rowSpan: 1,
            colSpan: 1,
          }
        : normalizeCell(cell, cellIndex)
    );
  }

  return (row?.cells || []).map((cell: any, cellIndex: number) => normalizeCell(cell, cellIndex));
};

const getCellText = (cell: any) => (typeof cell === 'string' ? cell : cell?.text || '');

const normalizeCell = (cell: any, cellIndex: number): UoWordTableCell => ({
  id: cell?.id || `cell-${cellIndex}`,
  text: getCellText(cell),
  rowSpan: Math.max(1, Number(cell?.rowSpan || 1)),
  colSpan: Math.max(1, Number(cell?.colSpan || 1)),
  editor: cell?.editor || 'text',
  signatureRole: cell?.signatureRole,
  signatureValue: cell?.signatureValue || (getCellText(cell).startsWith('data:image') ? getCellText(cell) : ''),
  lockedLabel: cell?.lockedLabel,
  style: cell?.style || {},
});

const createEmptyCell = (rowIndex: number, cellIndex: number, overrides: Partial<UoWordTableCell> = {}): UoWordTableCell => ({
  id: `cell-${Date.now()}-${rowIndex}-${cellIndex}-${Math.random().toString(36).slice(2, 8)}`,
  text: '',
  rowSpan: 1,
  colSpan: 1,
  style: {},
  editor: 'text',
  ...overrides,
});

const createEmptyParagraph = (): UoWordBlock => ({
  type: 'paragraph',
  id: createBlockId('p'),
  text: '',
  variant: 'body',
  source: 'user',
  style: {},
});

const normalizeWordBlocks = (blocks: any[] = [], templateKey?: UoReportType): UoWordBlock[] => {
  const normalizedBlocks = blocks
    .map((block, blockIndex) => {
      if (block?.type === 'paragraph') {
        return {
          type: 'paragraph',
          id: block.id || `p${blockIndex}`,
          text: block.text || '',
          variant: block.variant || 'body',
          source: block.source || 'template',
          style: block.style || {},
        } as UoWordBlock;
      }

      if (block?.type === 'spacer') {
        return {
          type: 'spacer',
          id: block.id || `spacer-${blockIndex}`,
          source: block.source || 'template',
          style: block.style || {},
        } as UoWordBlock;
      }

      if (block?.type === 'table') {
        return {
          type: 'table',
          id: block.id || `t${blockIndex}`,
          rows: (block.rows || []).map((row: any, rowIndex: number): UoWordTableRow => ({
            cells: getRowCells(row).map((cell, cellIndex) => ({
              ...cell,
              id: cell.id || `b${blockIndex}-r${rowIndex}-c${cellIndex}`,
            })),
          })),
          source: block.source || 'template',
          style: block.style || {},
        } as UoWordBlock;
      }

      return null;
    })
    .filter(Boolean) as UoWordBlock[];

  return templateKey ? annotateSignatureCells(templateKey, normalizedBlocks) : normalizedBlocks;
};

const mergeTemplateContent = (templates: UoDocumentTemplate[]) =>
  templates.reduce(
    (acc, template) => {
      if (!UO_TEMPLATE_BY_KEY[template.key]) {
        return acc;
      }

      acc[template.key] = {
        ...UO_TEMPLATE_BY_KEY[template.key],
        ...template,
        group: template.group,
        blocks: normalizeWordBlocks(template.blocks, template.key),
      };
      return acc;
    },
    { ...UO_TEMPLATE_BY_KEY } as Record<UoReportType, UoDocumentTemplate>,
  );

const getTableColumnCount = (block: UoWordTable) =>
  Math.max(1, ...block.rows.map((row) => row.cells.reduce((sum, cell) => sum + Math.max(1, cell.colSpan || 1), 0)));

const getSignatureColumnOverrides = (block: UoWordTable, cellIndex: number): Partial<UoWordTableCell> => {
  const signatureCell = block.rows
    .map((row) => row.cells[cellIndex])
    .find((cell) => cell?.editor === 'signature' && !cell.lockedLabel);

  if (!signatureCell) {
    return {};
  }

  return {
    editor: 'signature',
    signatureRole: signatureCell.signatureRole,
    lockedLabel: signatureCell.lockedLabel,
    signatureValue: '',
    text: signatureCell.lockedLabel || '',
  };
};

const tableHasHeader = (block: UoWordTable) => {
  const firstRow = block.rows[0]?.cells || [];
  if (firstRow.length <= 1 || isQuestionOrChoiceRow(getCellText(firstRow[0]))) {
    return false;
  }

  const firstCell = normalize(getCellText(firstRow[0]));
  const headerFirstCellHints = [
    '#',
    'nr.',
    'data',
    'server /',
    'echipament',
    'hostname',
    'parametru',
    'test efectuat',
    'element verificat',
    'unitate stocare',
    'tip comunicare',
    'persoana titulara',
    'nume si prenume',
    'obiect restaurat',
    'dimensiune',
  ];

  return headerFirstCellHints.some((hint) => firstCell === hint || firstCell.startsWith(hint));
};

const replaceToken = (value: string, token: string, replacement: string) => value.split(token).join(replacement);

const applyInterventionTokens = (value: string, number?: string, reportDate?: string) =>
  replaceToken(
    replaceToken(value, '{{interventionFileNumber}}', number || '{{interventionFileNumber}}'),
    '{{interventionDate}}',
    reportDate || '{{interventionDate}}'
  );

const applyInterventionNumber = (blocks: UoWordBlock[], number?: string, reportDate?: string) => {
  if (!number && !reportDate) {
    return blocks;
  }

  return blocks.map((block) => {
    if (block.type === 'paragraph') {
      return {
        ...block,
        text: applyInterventionTokens(block.text, number, reportDate),
      };
    }

    if (block.type === 'spacer') {
      return block;
    }

    return {
      ...block,
      rows: block.rows.map((row) => ({
        ...row,
        cells: row.cells.map((cell) => ({
          ...cell,
          text: applyInterventionTokens(cell.text, number, reportDate),
        })),
      })),
    };
  });
};

const PREVIEW_DEFAULT_CELL_FONT_SIZE = 7;
const PREVIEW_DEFAULT_CELL_PADDING_X = 3;
const PREVIEW_DEFAULT_CELL_PADDING_Y = 2.5;
const PREVIEW_EMPTY_CELL_LINE_HEIGHT = 13.4;
const PREVIEW_SIGNATURE_MAX_WIDTH = 120;

type PreviewTableCell = UoWordTableCell & { fillIn?: boolean };

const getDefaultCellFontSize = (template?: UoWordTable) => {
  const rows = template?.rows || [];
  for (const candidates of [rows.slice(1), rows]) {
    const counts = new Map<number, number>();
    candidates.forEach((row) =>
      row.cells.forEach((cell) => {
        const size = cell.style?.fontSize;
        if (size) {
          counts.set(size, (counts.get(size) || 0) + 1);
        }
      })
    );

    const mostUsed = Array.from(counts.entries()).sort((a, b) => b[1] - a[1])[0];
    if (mostUsed) {
      return mostUsed[0];
    }
  }

  return PREVIEW_DEFAULT_CELL_FONT_SIZE;
};

// Aceleasi reguli ca la exportul din backend: formatarea vine din sablonul Word, iar blocurile adaugate de utilizator o mostenesc.
const resolvePreviewBlocks = (blocks: UoWordBlock[], templateBlocks: UoWordBlock[]): UoWordBlock[] => {
  const templateBlocksById = new Map<string, UoWordBlock>();
  const templateCellsById = new Map<string, UoWordTableCell>();
  templateBlocks.forEach((block) => {
    if (block.id) {
      templateBlocksById.set(block.id, block);
    }

    if (block.type === 'table') {
      block.rows.forEach((row) => row.cells.forEach((cell) => templateCellsById.set(cell.id, cell)));
    }
  });

  return blocks.map((block) => {
    const candidate = templateBlocksById.get(block.id || '');
    const template = candidate?.type === block.type ? candidate : undefined;
    const style = template?.style || block.style || {};

    if (block.type === 'spacer') {
      return { ...block, style };
    }

    if (block.type === 'paragraph') {
      const isHeading = ['title', 'subtitle', 'section'].includes(block.variant || '');
      const hasKnownFormatting = !!template || !!style.fontSize;
      return {
        ...block,
        style: {
          ...style,
          fontSize: style.fontSize ?? (block.variant === 'title' ? 11 : isHeading ? 9 : 7.5),
          bold: hasKnownFormatting ? !!style.bold : isHeading,
        },
      };
    }

    const defaultFontSize = getDefaultCellFontSize(template?.type === 'table' ? template : undefined);
    const rows: UoWordTableRow[] = [];
    block.rows.forEach((row) => {
      const rowAbove = rows.length >= 2 ? rows[rows.length - 1] : undefined;
      rows.push({
        ...row,
        cells: row.cells.map((cell, cellIndex): PreviewTableCell => {
          const templateCell = templateCellsById.get(cell.id);
          const cellStyle = templateCell?.style || cell.style || {};
          const above = rowAbove?.cells[cellIndex]?.style;
          return {
            ...cell,
            fillIn: !templateCell || !templateCell.text?.trim(),
            style: {
              ...cellStyle,
              fontSize: cellStyle.fontSize ?? above?.fontSize ?? defaultFontSize,
              textAlign: cellStyle.textAlign ?? (templateCell ? undefined : above?.textAlign),
              verticalAlign: cellStyle.verticalAlign ?? above?.verticalAlign ?? 'center',
              width: cellStyle.width ?? (templateCell ? undefined : above?.width),
            },
          };
        }),
      });
    });

    return { ...block, rows, style };
  });
};

const parseWidthPoints = (width?: string) => {
  const pixels = width?.endsWith('px') ? Number(width.slice(0, -2)) : NaN;
  return Number.isFinite(pixels) && pixels > 0 ? pixels * 0.75 : 0;
};

// Latimile coloanelor in procente, calculate din latimile celulelor din sablon.
const getPreviewColumnWidths = (table: UoWordTable) => {
  const columnCount = Math.max(1, ...table.rows.map((row) => row.cells.reduce((sum, cell) => sum + Math.max(1, cell.colSpan || 1), 0)));
  const occupied = new Set<string>();
  const placements: { column: number; colSpan: number; width: number }[] = [];

  table.rows.forEach((row, rowIndex) => {
    let column = 0;
    row.cells.forEach((cell) => {
      while (column < columnCount && occupied.has(`${rowIndex}:${column}`)) {
        column++;
      }

      if (column >= columnCount) {
        return;
      }

      const colSpan = Math.min(Math.max(1, cell.colSpan || 1), columnCount - column);
      const rowSpan = Math.max(1, cell.rowSpan || 1);
      for (let r = rowIndex; r < rowIndex + rowSpan; r++) {
        for (let c = column; c < column + colSpan; c++) {
          occupied.add(`${r}:${c}`);
        }
      }

      placements.push({ column, colSpan, width: parseWidthPoints(cell.style?.width) });
      column += colSpan;
    });
  });

  const widths: number[] = new Array(columnCount).fill(0);
  placements
    .filter((placement) => placement.width > 0)
    .sort((a, b) => a.colSpan - b.colSpan)
    .forEach((placement) => {
      for (let c = placement.column; c < placement.column + placement.colSpan; c++) {
        if (widths[c] <= 0) {
          widths[c] = placement.width / placement.colSpan;
        }
      }
    });

  const known = widths.filter((width) => width > 0);
  const fallback = known.length > 0 ? known.reduce((sum, width) => sum + width, 0) / known.length : 1;
  const filled = widths.map((width) => (width > 0 ? width : fallback));
  const total = filled.reduce((sum, width) => sum + width, 0);
  return filled.map((width) => (width * 100) / total);
};

const getPreviewRowMinHeight = (table: UoWordTable, rowIndex: number) => {
  if (table.style?.rowHeight) {
    return rowIndex > 0 ? table.style.rowHeight : undefined;
  }

  return table.rows[rowIndex].cells.some((cell: PreviewTableCell) => cell.fillIn) ? PREVIEW_EMPTY_CELL_LINE_HEIGHT : undefined;
};

const toCssVerticalAlign = (value?: string) => {
  const normalized = (value || '').toLowerCase();
  if (normalized === 'center' || normalized === 'middle') {
    return 'middle';
  }

  return normalized === 'bottom' ? 'bottom' : 'top';
};

const UORapoarte: React.FC = () => {
  const { message } = App.useApp();
  const { profile } = useAuth();
  const isAdmin = (profile?.userPermission?.rols || []).includes('admin');
  const [selectedTemplateKey, setSelectedTemplateKey] = useState<UoReportType>(initialTemplateKey);
  const [selectedMonth, setSelectedMonth] = useState<Dayjs>(dayjs());
  const [selectedDate, setSelectedDate] = useState<Dayjs>(dayjs());
  const [quarter, setQuarter] = useState<number>(Math.floor(dayjs().month() / 3) + 1);
  const [reports, setReports] = useState<UoReport[]>([]);
  const [selectedReport, setSelectedReport] = useState<UoReport | null>(null);
  const [templateContents, setTemplateContents] = useState<Record<UoReportType, UoDocumentTemplate>>(UO_TEMPLATE_BY_KEY);
  const [documentBlocks, setDocumentBlocks] = useState<UoWordBlock[]>([]);
  const [selectedCell, setSelectedCell] = useState<SelectedTableCell | null>(null);
  const [attachments, setAttachments] = useState<UploadFile[]>([]);
  const [technicianSignature, setTechnicianSignature] = useState<string>('');
  const [beneficiarySignature, setBeneficiarySignature] = useState<string>('');
  const [previewRecord, setPreviewRecord] = useState<UoReport | null>(null);
  const [previewMode, setPreviewMode] = useState<'word' | 'pdf'>('word');
  const [previewPdfUrl, setPreviewPdfUrl] = useState<string>('');
  const [previewLoading, setPreviewLoading] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(false);
  const [templatesLoading, setTemplatesLoading] = useState<boolean>(false);
  const [saving, setSaving] = useState<boolean>(false);

  const selectedTemplate = templateContents[selectedTemplateKey] || UO_TEMPLATE_BY_KEY[selectedTemplateKey];
  const isWordLikeDocument = isStaticDocumentTemplate(selectedTemplateKey);
  const hasInlineDocumentSignatures = hasInlineDocumentSignatureFields(documentBlocks);
  const isFinalized = selectedReport?.status === 'signed' || selectedReport?.status === 'sent';
  const isLocked = false;
  const getTemplateBlocks = useCallback(
    (templateKey: UoReportType) => cloneWordBlocks(templateContents[templateKey]?.blocks || UO_TEMPLATE_BY_KEY[templateKey]?.blocks || []),
    [templateContents],
  );

  const fetchReports = useCallback(async () => {
    setLoading(true);
    try {
      const searchPayload = buildSearchPayload(selectedTemplate, selectedMonth, selectedDate, quarter);
      const reportsResponse = await UoReportService.search(searchPayload);

      setReports((reportsResponse as ControllerResponse<UoReport[]>)?.response || []);
    } catch (error: any) {
      message.error(error?.message || 'Nu s-au putut incarca documentele UO.');
    } finally {
      setLoading(false);
    }
  }, [selectedTemplate, selectedMonth, selectedDate, quarter, message]);

  useEffect(() => {
    fetchReports();
  }, [fetchReports]);

  useEffect(() => {
    return () => {
      if (previewPdfUrl) {
        URL.revokeObjectURL(previewPdfUrl);
      }
    };
  }, [previewPdfUrl]);

  const fetchTemplateContents = useCallback(async () => {
    setTemplatesLoading(true);
    try {
      const response = (await UoReportService.templates()) as ControllerResponse<UoDocumentTemplate[]>;
      const templates = response?.response || [];
      setTemplateContents(mergeTemplateContent(templates));
    } catch (error: any) {
      message.error(error?.message || 'Nu s-au putut incarca template-urile UO.');
    } finally {
      setTemplatesLoading(false);
    }
  }, [message]);

  useEffect(() => {
    fetchTemplateContents();
  }, [fetchTemplateContents]);

  const resetEditor = useCallback(
    (templateKey = selectedTemplateKey) => {
      setSelectedReport(null);
      setDocumentBlocks(getTemplateBlocks(templateKey));
      setSelectedCell(null);
      setAttachments([]);
      setTechnicianSignature('');
      setBeneficiarySignature('');
    },
    [getTemplateBlocks, selectedTemplateKey]
  );

  // Fill an empty editor once the backend templates arrive. This must not reset the editor,
  // otherwise a report opened before the templates loaded would be discarded.
  useEffect(() => {
    setDocumentBlocks((current) => (current.length ? current : getTemplateBlocks(selectedTemplateKey)));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [getTemplateBlocks]);

  const changeTemplate = (templateKey: UoReportType) => {
    setSelectedTemplateKey(templateKey);
    resetEditor(templateKey);
  };

  const templateOptions = useMemo(
    () =>
      UO_DOCUMENT_TEMPLATES.map((template) => ({
        label: `${template.shortLabel} - ${template.label.replace(`${template.shortLabel} - `, '')}`,
        value: template.key,
      })),
    []
  );

  const loadReport = (record: UoReport) => {
    const payload = record.payload || {};
    const templateKey = (payload.templateKey || record.type || initialTemplateKey) as UoReportType;
    const template = templateContents[templateKey] || UO_TEMPLATE_BY_KEY[templateKey] || UO_TEMPLATE_BY_KEY[initialTemplateKey];

    setSelectedTemplateKey(template.key);
    setSelectedReport(record);
    setSelectedCell(null);
    setDocumentBlocks(
      hydrateInlineDocumentSignatures(
        applyInterventionNumber(
          normalizeWordBlocks(cloneWordBlocks(payload.wordBlocks || getTemplateBlocks(template.key)), template.key),
          record.number,
          record.reportDate ? dayjs(record.reportDate).format(DATE_FORMAT) : undefined
        ),
        record.technicianSignature,
        record.beneficiarySignature
      )
    );
    setAttachments(
      (payload.attachments || []).map((file: UploadFile) => ({
        uid: file.uid || file.name,
        name: file.name,
        status: 'done',
        size: file.size,
        type: file.type,
        url: file.url,
      }))
    );
    setTechnicianSignature(record.technicianSignature || '');
    setBeneficiarySignature(record.beneficiarySignature || '');

    if (record.reportDate) {
      const reportDate = dayjs(record.reportDate);
      setSelectedDate(reportDate);
      setSelectedMonth(reportDate);
    }

    if (record.quarter) {
      setQuarter(record.quarter);
    }
  };

  const updateParagraph = (blockIndex: number, text: string) => {
    setDocumentBlocks((current) =>
      current.map((block, index) => (index === blockIndex && block.type === 'paragraph' ? { ...block, text } : block))
    );
  };

  const insertParagraphBlock = (blockIndex: number, placement: 'before' | 'after') => {
    setDocumentBlocks((current) => {
      const next = [...current];
      next.splice(placement === 'before' ? blockIndex : blockIndex + 1, 0, createEmptyParagraph());
      return next;
    });
  };

  const removeParagraphBlock = (blockIndex: number) => {
    setDocumentBlocks((current) => current.filter((_, index) => index !== blockIndex));
  };

  const updateTableCell = (blockIndex: number, rowIndex: number, cellIndex: number, value: string) => {
    setDocumentBlocks((current) =>
      current.map((block, index) => {
        if (index !== blockIndex || block.type !== 'table') {
          return block;
        }

        return {
          ...block,
          rows: block.rows.map((row, rIndex) => ({
            ...row,
            cells: row.cells.map((cell, cIndex) => (rIndex === rowIndex && cIndex === cellIndex ? { ...cell, text: value } : cell)),
          })),
        };
      })
    );
  };

  const updateSignatureCell = (blockIndex: number, rowIndex: number, cellIndex: number, value: string) => {
    setDocumentBlocks((current) =>
      current.map((block, index) => {
        if (index !== blockIndex || block.type !== 'table') {
          return block;
        }

        return {
          ...block,
          rows: block.rows.map((row, rIndex) => ({
            ...row,
            cells: row.cells.map((cell, cIndex) =>
              rIndex === rowIndex && cIndex === cellIndex ? { ...cell, signatureValue: value } : cell
            ),
          })),
        };
      })
    );
  };

  const addTableRow = (blockIndex: number, afterRowIndex?: number) => {
    setDocumentBlocks((current) =>
      current.map((block, index) => {
        if (index !== blockIndex || block.type !== 'table') {
          return block;
        }

        const columnCount = getTableColumnCount(block);
        const insertIndex = typeof afterRowIndex === 'number' ? Math.min(afterRowIndex + 1, block.rows.length) : block.rows.length;
        const referenceRow = insertIndex - 1 >= 1 ? block.rows[insertIndex - 1] : block.rows[insertIndex];
        const newRow = {
          cells: Array.from({ length: columnCount }, (_, cellIndex) => {
            const { textAlign, verticalAlign, width, fontSize } = referenceRow?.cells[cellIndex]?.style || {};
            return createEmptyCell(insertIndex, cellIndex, {
              style: { textAlign, verticalAlign, width, fontSize },
              ...getSignatureColumnOverrides(block, cellIndex),
            });
          }),
        };
        const rows = [...block.rows];
        rows.splice(insertIndex, 0, newRow);

        return {
          ...block,
          rows,
        };
      })
    );
  };

  const removeTableRow = (blockIndex: number, rowIndex: number) => {
    setDocumentBlocks((current) =>
      current.map((block, index) => {
        if (index !== blockIndex || block.type !== 'table' || block.rows.length <= 1) {
          return block;
        }

        return { ...block, rows: block.rows.filter((_, index) => index !== rowIndex) };
      })
    );
  };

  const addTableColumn = (blockIndex: number, afterCellIndex?: number) => {
    setDocumentBlocks((current) =>
      current.map((block, index) =>
        index === blockIndex && block.type === 'table'
          ? {
              ...block,
              rows: block.rows.map((row, rowIndex) => ({
                ...row,
                cells: (() => {
                  const insertIndex =
                    typeof afterCellIndex === 'number' ? Math.min(afterCellIndex + 1, row.cells.length) : row.cells.length;
                  const cells = [...row.cells];
                  cells.splice(insertIndex, 0, createEmptyCell(rowIndex, insertIndex));
                  return cells;
                })(),
              })),
            }
          : block
      )
    );
  };

  const removeTableColumn = (blockIndex: number, cellIndex?: number) => {
    setDocumentBlocks((current) =>
      current.map((block, index) => {
        if (index !== blockIndex || block.type !== 'table') {
          return block;
        }

        const maxColumns = getTableColumnCount(block);
        if (maxColumns <= 1) {
          return block;
        }

        const targetCellIndex = typeof cellIndex === 'number' ? cellIndex : maxColumns - 1;
        return {
          ...block,
          rows: block.rows.map((row) => {
            if (row.cells.length === 0) {
              return row;
            }

            const cells = [...row.cells];
            const boundedCellIndex = Math.min(targetCellIndex, cells.length - 1);
            const targetCell = cells[boundedCellIndex];
            if ((targetCell.colSpan || 1) > 1) {
              cells[boundedCellIndex] = { ...targetCell, colSpan: Math.max(1, (targetCell.colSpan || 1) - 1) };
            } else if (cells.length > 1) {
              cells.splice(boundedCellIndex, 1);
            }

            return { ...row, cells };
          }),
        };
      })
    );
    setSelectedCell((current) => (current?.blockIndex === blockIndex ? null : current));
  };

  const addAttachment = async (file: RcFile) => {
    try {
      const dataUrl = await fileToDataUrl(file);
      setAttachments((current) => [
        ...current,
        {
          uid: file.uid,
          name: file.name,
          status: 'done',
          size: file.size,
          type: file.type,
          url: dataUrl,
        },
      ]);
    } catch {
      message.error('Fisierul nu a putut fi incarcat in document.');
    }
  };

  const validateBeforeSave = (sign: boolean) => {
    if (sign) {
      if (hasInlineDocumentSignatures) {
        const inlineSignatures = extractInlineDocumentSignatures(documentBlocks);
        const requiresBeneficiarySignature = selectedTemplateKey !== 'visit';
        if (!inlineSignatures.technician || (requiresBeneficiarySignature && !inlineSignatures.beneficiary)) {
          message.error(requiresBeneficiarySignature ? 'Pentru semnare sunt necesare ambele semnaturi din document.' : 'Pentru semnare este necesara semnatura tehnicianului din document.');
          return false;
        }
      } else if (!technicianSignature || !beneficiarySignature) {
        message.error('Pentru semnare sunt necesare ambele semnaturi.');
        return false;
      }
    }

    if (selectedTemplateKey === 'upsTest') {
      const text = JSON.stringify(documentBlocks).toLowerCase();
      if (text.includes('____ % (____ w)') || text.includes('____ % (max. admis')) {
        message.error('Pentru Anexa 4 completeaza obligatoriu incarcatura UPS #1/#2 si statusurile OK/NOK.');
        return false;
      }
    }

    return true;
  };

  const buildReportPayload = (sign: boolean): UoReport => {
    const reportDate =
      selectedTemplate.periodMode === 'quarter'
        ? dayjs(new Date(selectedMonth.year(), (quarter - 1) * 3, 1))
        : selectedTemplate.periodMode === 'month'
          ? selectedMonth.startOf('month')
          : selectedDate;

    const inlineSignatures = extractInlineDocumentSignatures(documentBlocks);
    const finalTechnicianSignature = inlineSignatures.technician || technicianSignature;
    const finalBeneficiarySignature = inlineSignatures.beneficiary || beneficiarySignature;

    const payload = {
      templateKey: selectedTemplate.key,
      templateFile: selectedTemplate.templateFile,
      templateLabel: selectedTemplate.label,
      periodMode: selectedTemplate.periodMode,
      wordBlocksVersion: 3,
      wordBlocks: applyInterventionNumber(
        documentBlocks,
        selectedReport?.number,
        reportDate.format(DATE_FORMAT)
      ),
      attachments: attachments.map((file) => ({
        uid: file.uid,
        name: file.name,
        size: file.size,
        type: file.type,
        url: file.url,
      })),
    };

    return {
      id: selectedReport?.id,
      type: selectedTemplate.key,
      status: sign ? 'signed' : selectedReport?.status || 'draft',
      reportDate: reportDate.toISOString(),
      month: selectedTemplate.periodMode === 'none' || selectedTemplate.periodMode === 'quarter' ? undefined : reportDate.month() + 1,
      year: selectedTemplate.periodMode === 'none' ? undefined : reportDate.year(),
      quarter: selectedTemplate.periodMode === 'quarter' ? quarter : undefined,
      technicianSignature: finalTechnicianSignature,
      beneficiarySignature: finalBeneficiarySignature,
      templateName: selectedTemplate.templateFile,
      templateVersion: selectedTemplate.templateFile,
      payload,
    };
  };

  const saveReport = async (sign: boolean) => {
    if (!validateBeforeSave(sign)) {
      return;
    }

    setSaving(true);
    try {
      const reportPayload = buildReportPayload(sign);
      const response = (await UoReportService.upsert(reportPayload)) as ControllerResponse<UoReport>;
      if (response?.errors?.length) {
        message.error(response.errors[0]);
        return;
      }

      message.success(sign ? 'Document semnat.' : 'Draft salvat.');
      if (response.response) {
        loadReport(response.response);
      }
      await fetchReports();
    } catch (error: any) {
      message.error(error?.message || 'Documentul nu a putut fi salvat.');
    } finally {
      setSaving(false);
    }
  };

  const markSent = async (record: UoReport) => {
    if (!record.id) return;
    setSaving(true);
    try {
      const response = (await UoReportService.markSent(record.id)) as ControllerResponse<UoReport>;
      if (response?.errors?.length) {
        message.error(response.errors[0]);
        return;
      }
      message.success('Document marcat ca trimis.');
      await fetchReports();
    } catch (error: any) {
      message.error(error?.message || 'Documentul nu a putut fi marcat ca trimis.');
    } finally {
      setSaving(false);
    }
  };

  const exportReport = async (record: UoReport, format: 'docx' | 'pdf') => {
    if (!record.id) return;
    setSaving(true);
    try {
      const response = await UoReportService.export(record.id, format);
      const blob = await readExportBlob(response);
      saveBlobCrossPlatform(blob, getFilenameFromResponse(response, `UO_${record.type}.${format}`));
    } catch (error: any) {
      message.error(error?.message || 'Exportul nu a putut fi generat.');
    } finally {
      setSaving(false);
    }
  };

  const previewWordReport = (record: UoReport) => {
    if (previewPdfUrl) {
      URL.revokeObjectURL(previewPdfUrl);
      setPreviewPdfUrl('');
    }
    setPreviewMode('word');
    setPreviewRecord(record);
  };

  const previewPdfReport = async (record: UoReport) => {
    if (!record.id) return;
    setPreviewMode('pdf');
    setPreviewRecord(record);
    setPreviewLoading(true);
    try {
      const response = await UoReportService.export(record.id, 'pdf');
      const blob = await readExportBlob(response);
      if (previewPdfUrl) {
        URL.revokeObjectURL(previewPdfUrl);
      }
      setPreviewPdfUrl(URL.createObjectURL(blob));
    } catch (error: any) {
      message.error(error?.message || 'Preview-ul PDF nu a putut fi generat.');
    } finally {
      setPreviewLoading(false);
    }
  };

  const closePreview = () => {
    setPreviewRecord(null);
    setPreviewMode('word');
    if (previewPdfUrl) {
      URL.revokeObjectURL(previewPdfUrl);
      setPreviewPdfUrl('');
    }
  };

  const deleteReport = async (record: UoReport) => {
    if (!record.id) return;
    setSaving(true);
    try {
      const response = (await UoReportService.delete(record.id)) as ControllerResponse<boolean>;
      if (response?.errors?.length) {
        message.error(response.errors[0]);
        return;
      }

      message.success('Document sters.');
      if (selectedReport?.id === record.id) {
        resetEditor();
      }
      if (previewRecord?.id === record.id) {
        closePreview();
      }
      await fetchReports();
    } catch (error: any) {
      message.error(error?.message || 'Documentul nu a putut fi sters.');
    } finally {
      setSaving(false);
    }
  };

  const columns: ColumnsType<UoReport> = [
    {
      title: 'Numar',
      dataIndex: 'number',
      width: 120,
      render: (value) => value || '-',
    },
    {
      title: 'Document',
      dataIndex: 'type',
      width: 240,
      render: (value: UoReportType, record) => record.payload?.templateLabel || UO_TEMPLATE_BY_KEY[value]?.label || value,
    },
    {
      title: 'Status',
      dataIndex: 'status',
      width: 110,
      render: statusTag,
    },
    {
      title: 'Perioada',
      key: 'period',
      width: 120,
      render: (_, record) => buildPeriodLabel(record),
    },
    {
      title: 'Semnat',
      key: 'signed',
      width: 120,
      render: (_, record) => (record.signedAt ? dayjs(record.signedAt).format('DD.MM.YYYY') : '-'),
    },
    {
      title: 'Actiuni',
      key: 'actions',
      width: 620,
      render: (_, record) => (
        <div className='uo-table-actions'>
          <Button size='small' onClick={() => loadReport(record)}>
            Editeaza
          </Button>
          <Button size='small' icon={<EyeOutlined />} onClick={() => previewWordReport(record)}>
            Word
          </Button>
          <Button size='small' icon={<EyeOutlined />} onClick={() => previewPdfReport(record)}>
            PDF
          </Button>
          <Button size='small' icon={<DownloadOutlined />} onClick={() => exportReport(record, 'docx')}>
            DOCX
          </Button>
          <Button size='small' icon={<DownloadOutlined />} onClick={() => exportReport(record, 'pdf')}>
            PDF
          </Button>
          {isAdmin && (
            <Button size='small' icon={<SendOutlined />} disabled={record.status === 'sent'} onClick={() => markSent(record)}>
              Trimis
            </Button>
          )}
          {isAdmin && (
            <Popconfirm
              title='Stergi documentul?'
              description='Operatiunea nu poate fi anulata.'
              okText='Sterge'
              cancelText='Renunta'
              onConfirm={() => deleteReport(record)}
            >
              <Button size='small' danger icon={<DeleteOutlined />}>
                Sterge
              </Button>
            </Popconfirm>
          )}
        </div>
      ),
    },
  ];

  function renderPeriodControls() {
    if (selectedTemplate.periodMode === 'quarter') {
      return (
        <>
          <DatePicker picker='year' value={selectedMonth} onChange={(value) => value && setSelectedMonth(value)} />
          <Select
            value={quarter}
            onChange={setQuarter}
            style={{ minWidth: 110 }}
            options={[1, 2, 3, 4].map((item) => ({ value: item, label: `T${item}` }))}
          />
        </>
      );
    }

    if (selectedTemplate.periodMode === 'month') {
      return <DatePicker picker='month' value={selectedMonth} onChange={(value) => value && setSelectedMonth(value)} />;
    }

    if (selectedTemplate.periodMode === 'date') {
      return <DatePicker value={selectedDate} onChange={(value) => value && setSelectedDate(value)} />;
    }

    return <Tag color='default'>Document configurabil fara perioada fixa</Tag>;
  }

  function renderEditor() {
    return (
      <Spin spinning={loading || templatesLoading || saving}>
        <div className='uo-editor-layout'>
          <aside className='uo-side-panel'>
            <div className='uo-field-label'>Model document</div>
            <Select
              value={selectedTemplateKey}
              options={templateOptions}
              onChange={changeTemplate}
              style={{ width: '100%' }}
              showSearch
              optionFilterProp='label'
            />

            <div className='uo-field-label'>Perioada</div>
            <Space direction='vertical' style={{ width: '100%' }}>
              {renderPeriodControls()}
              <Tag color={isFinalized ? 'blue' : 'orange'}>
                {isFinalized ? 'Editare activa pentru document semnat/trimis' : selectedTemplate.deadline}
              </Tag>
            </Space>

            <Divider />
            <Space direction='vertical' style={{ width: '100%' }}>
              <Button icon={<PlusOutlined />} onClick={() => resetEditor()} block>
                Document nou
              </Button>
              <Button icon={<ReloadOutlined />} onClick={fetchReports} block>
                Refresh
              </Button>
            </Space>

            <Divider />
            <Text type='secondary'>Template sursa</Text>
            <div className='uo-template-file'>{selectedTemplate.templateFile}</div>
          </aside>

          <main className='uo-editor-main'>
            <div className='uo-document-header'>
              <div>
                <Text strong>{selectedTemplate.label}</Text>
                <div>
                  {selectedReport?.number ? <Tag color='processing'>{selectedReport.number}</Tag> : <Tag>nesalvat</Tag>}
                  {statusTag(selectedReport?.status)}
                </div>
              </div>
              <Space wrap>
                <Button type='primary' icon={<SaveOutlined />} loading={saving} disabled={isLocked} onClick={() => saveReport(false)}>
                  Salveaza draft
                </Button>
                <Button icon={<FileDoneOutlined />} loading={saving} disabled={isLocked} onClick={() => saveReport(true)}>
                  Salveaza si semneaza
                </Button>
              </Space>
            </div>

            <div className='uo-word-page'>
              {documentBlocks.map((block, index) => renderBlock(block, index))}
              {renderAttachments()}
              {!hasInlineDocumentSignatures && renderSignatures()}
            </div>

            <Divider />
            <section>
              <div className='uo-section-title'>Documente salvate pentru modelul selectat</div>
              <Table<UoReport>
                rowKey={(record) => record.id || record.number || `${record.type}-${record.createDate}`}
                columns={columns}
                dataSource={reports}
                pagination={{ pageSize: 8 }}
                scroll={{ x: true }}
              />
            </section>
          </main>
        </div>
      </Spin>
    );
  }

  function renderBlock(block: UoWordBlock, blockIndex: number) {
    if (block.type === 'paragraph') {
      if (!isWordLikeDocument) {
        return (
          <div
            key={block.id || `p-${blockIndex}`}
            className={`uo-static-paragraph uo-static-${block.variant || 'body'}`}
            style={{ textAlign: block.style?.textAlign as any }}
          >
            {block.text}
          </div>
        );
      }

      return (
        <div key={block.id || `p-${blockIndex}`} className='uo-word-block'>
          {!isLocked && (
            <div className='uo-block-toolbar'>
              <Button size='small' icon={<PlusOutlined />} onClick={() => insertParagraphBlock(blockIndex, 'before')}>
                Text inainte
              </Button>
              <Button size='small' icon={<PlusOutlined />} onClick={() => insertParagraphBlock(blockIndex, 'after')}>
                Text dupa
              </Button>
              {block.source === 'user' && (
                <Button size='small' danger icon={<DeleteOutlined />} onClick={() => removeParagraphBlock(blockIndex)}>
                  Sterge
                </Button>
              )}
            </div>
          )}
          <TextArea
            className={`uo-word-paragraph uo-word-${block.variant || 'body'}`}
            value={block.text}
            disabled={isLocked}
            autoSize={{ minRows: block.variant === 'title' ? 1 : 2, maxRows: 12 }}
            style={{ textAlign: block.style?.textAlign as any }}
            onChange={(event) => updateParagraph(blockIndex, event.target.value)}
          />
        </div>
      );
    }

    if (block.type === 'spacer') {
      return <div key={`spacer-${blockIndex}`} className='uo-word-spacer' />;
    }

    return renderEditableTable(block, blockIndex);
  }

  function renderEditableTable(block: UoWordTable, blockIndex: number) {
    const hasHeader = tableHasHeader(block);
    const selected = selectedCell?.blockIndex === blockIndex ? selectedCell : null;
    const selectedRow = selected?.rowIndex ?? block.rows.length - 1;
    const selectedColumn = selected?.cellIndex ?? Math.max(0, (block.rows[0]?.cells.length || 1) - 1);

    return (
      <div className='uo-word-table-wrap' key={block.id || `t-${blockIndex}`}>
        {!isLocked && (
          <div className='uo-table-toolbar'>
            {isWordLikeDocument ? (
              <>
                <Button size='small' icon={<PlusOutlined />} onClick={() => insertParagraphBlock(blockIndex, 'before')}>
                  Text inainte
                </Button>
                <Button size='small' icon={<PlusOutlined />} onClick={() => insertParagraphBlock(blockIndex, 'after')}>
                  Text sub tabel
                </Button>
                <Button size='small' icon={<PlusOutlined />} onClick={() => addTableRow(blockIndex, selectedRow)}>
                  Rand dupa
                </Button>
                <Button size='small' icon={<PlusOutlined />} onClick={() => addTableColumn(blockIndex, selectedColumn)}>
                  Coloana dupa
                </Button>
                <Button size='small' danger icon={<DeleteOutlined />} disabled={block.rows.length <= 1} onClick={() => removeTableRow(blockIndex, selectedRow)}>
                  Sterge rand
                </Button>
                <Button size='small' danger icon={<DeleteOutlined />} onClick={() => removeTableColumn(blockIndex, selectedColumn)}>
                  Sterge coloana
                </Button>
              </>
            ) : (
              <>
                <Button size='small' icon={<PlusOutlined />} onClick={() => addTableRow(blockIndex)}>
                  Rand
                </Button>
                <Button size='small' icon={<PlusOutlined />} onClick={() => addTableColumn(blockIndex)}>
                  Coloana
                </Button>
                <Button size='small' icon={<DeleteOutlined />} onClick={() => removeTableColumn(blockIndex)}>
                  Ultima coloana
                </Button>
              </>
            )}
          </div>
        )}
        <table className='uo-word-table'>
          <tbody>
            {block.rows.map((row, rowIndex) => (
              <tr key={`${blockIndex}-${rowIndex}`} className={hasHeader && rowIndex === 0 ? 'uo-word-table-head' : undefined}>
                {row.cells.map((cell, cellIndex) => (
                  <td
                    key={cell.id || `${blockIndex}-${rowIndex}-${cellIndex}`}
                    rowSpan={cell.rowSpan && cell.rowSpan > 1 ? cell.rowSpan : undefined}
                    colSpan={cell.colSpan && cell.colSpan > 1 ? cell.colSpan : undefined}
                    style={{
                      textAlign: cell.style?.textAlign as any,
                      verticalAlign: cell.style?.verticalAlign as any,
                      background: cell.style?.background,
                      width: cell.style?.width,
                      fontWeight: cell.style?.bold || (hasHeader && rowIndex === 0) ? 700 : undefined,
                    }}
                    className={selected?.rowIndex === rowIndex && selected?.cellIndex === cellIndex ? 'uo-selected-cell' : undefined}
                  >
                    {renderTableCell(block, blockIndex, rowIndex, cellIndex, cell)}
                  </td>
                ))}
                {!isLocked && (
                  <td className='uo-row-action-cell'>
                    <Button
                      size='small'
                      type='text'
                      icon={<DeleteOutlined />}
                      disabled={block.rows.length <= 1}
                      onClick={() => removeTableRow(blockIndex, rowIndex)}
                    />
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  }

  function renderTableCell(block: UoWordTable, blockIndex: number, rowIndex: number, cellIndex: number, cell: UoWordTableCell) {
    if (cell.editor === 'signature') {
      const signature = getSignatureValue(cell);
      return (
        <div className='uo-inline-signature-cell' onFocus={() => setSelectedCell({ blockIndex, rowIndex, cellIndex })} onClick={() => setSelectedCell({ blockIndex, rowIndex, cellIndex })}>
          {cell.lockedLabel && <div className='uo-cell-label'>{cell.lockedLabel}</div>}
          {isLocked && signature ? (
            <img className='uo-signature-preview' src={signature} alt='Semnatura' />
          ) : (
            <SignaturePad valueSignature={signature} onSignatureDrawn={(value) => updateSignatureCell(blockIndex, rowIndex, cellIndex, value)} />
          )}
        </div>
      );
    }

    return (
      <TextArea
        value={cell.text}
        disabled={isLocked}
        autoSize={{ minRows: 1, maxRows: 10 }}
        onFocus={() => setSelectedCell({ blockIndex, rowIndex, cellIndex })}
        onClick={() => setSelectedCell({ blockIndex, rowIndex, cellIndex })}
        onChange={(event) => updateTableCell(blockIndex, rowIndex, cellIndex, event.target.value)}
      />
    );
  }

  function renderAttachments() {
    return (
      <section className='uo-word-section'>
        <div className='uo-section-title'>Atasamente / poze / documente suport</div>
        <Upload
          fileList={attachments}
          beforeUpload={(file) => {
            void addAttachment(file);
            return Upload.LIST_IGNORE;
          }}
          onRemove={(file) => {
            setAttachments((current) => current.filter((item) => item.uid !== file.uid));
          }}
          disabled={isLocked}
        >
          {!isLocked && <Button icon={<UploadOutlined />}>Incarca fisier</Button>}
        </Upload>
      </section>
    );
  }

  function renderSignatures() {
    return (
      <section className='uo-word-section'>
        <div className='uo-section-title'>Semnaturi</div>
        <div className='uo-signature-grid'>
          <div className='uo-signature-box'>
            <div className='uo-section-title'>Tehnician Prestator</div>
            {isLocked && technicianSignature ? (
              <img className='uo-signature-preview' src={technicianSignature} alt='Semnatura tehnician' />
            ) : (
              <SignaturePad valueSignature={technicianSignature} onSignatureDrawn={setTechnicianSignature} />
            )}
          </div>
          <div className='uo-signature-box'>
            <div className='uo-section-title'>Reprezentant Beneficiar</div>
            {isLocked && beneficiarySignature ? (
              <img className='uo-signature-preview' src={beneficiarySignature} alt='Semnatura beneficiar' />
            ) : (
              <SignaturePad valueSignature={beneficiarySignature} onSignatureDrawn={setBeneficiarySignature} />
            )}
          </div>
        </div>
      </section>
    );
  }

  function renderPreview() {
    if (!previewRecord) {
      return null;
    }

    if (previewMode === 'pdf') {
      return (
        <Spin spinning={previewLoading}>
          {previewPdfUrl ? (
            <iframe className='uo-pdf-preview' src={previewPdfUrl} title='Preview PDF UO' />
          ) : (
            <Empty description='PDF-ul se genereaza...' />
          )}
        </Spin>
      );
    }

    const payload = previewRecord.payload || {};
    const templateKey = (payload.templateKey || previewRecord.type || initialTemplateKey) as UoReportType;
    const template = templateContents[templateKey] || UO_TEMPLATE_BY_KEY[templateKey] || UO_TEMPLATE_BY_KEY[initialTemplateKey];
    const blocks = resolvePreviewBlocks(
      hydrateInlineDocumentSignatures(
        applyInterventionNumber(
          normalizeWordBlocks(cloneWordBlocks(payload.wordBlocks || getTemplateBlocks(template.key)), template.key),
          previewRecord.number,
          previewRecord.reportDate ? dayjs(previewRecord.reportDate).format(DATE_FORMAT) : undefined
        ),
        previewRecord.technicianSignature,
        previewRecord.beneficiarySignature
      ),
      template.blocks || []
    );
    const previewHasInlineSignatures = hasInlineDocumentSignatureFields(blocks);

    return (
      <div className='uo-word-preview-scroll'>
        <div className='uo-word-preview-page'>
          {blocks.map((block, index) => renderPreviewBlock(block, index))}
          {renderPreviewAttachments(payload.attachments || [])}
          {!previewHasInlineSignatures && renderPreviewSignature('Tehnician Prestator', previewRecord.technicianSignature)}
          {!previewHasInlineSignatures && renderPreviewSignature('Reprezentant Beneficiar', previewRecord.beneficiarySignature)}
        </div>
      </div>
    );
  }

  function renderPreviewBlock(block: UoWordBlock, index: number) {
    if (block.type === 'paragraph') {
      return (
        <div
          key={`preview-p-${index}`}
          className='uo-preview-paragraph'
          style={{
            fontSize: `${block.style?.fontSize}pt`,
            fontWeight: block.style?.bold ? 700 : 400,
            fontStyle: block.style?.italic ? 'italic' : undefined,
            textAlign: block.style?.textAlign as any,
            margin: `${block.style?.spacingBefore || 0}pt 0 ${block.style?.spacingAfter || 0}pt`,
          }}
        >
          {block.text}
        </div>
      );
    }

    if (block.type === 'spacer') {
      return <div key={`preview-spacer-${index}`} className={block.style?.pageBreak ? 'uo-preview-page-break' : 'uo-preview-spacer'} />;
    }

    const columnWidths = getPreviewColumnWidths(block);
    const paddingX = block.style?.cellPaddingX ?? PREVIEW_DEFAULT_CELL_PADDING_X;
    const paddingY = block.style?.cellPaddingY ?? PREVIEW_DEFAULT_CELL_PADDING_Y;

    return (
      <table className='uo-preview-table' key={`preview-t-${index}`}>
        <colgroup>
          {columnWidths.map((width, columnIndex) => (
            <col key={columnIndex} style={{ width: `${width}%` }} />
          ))}
        </colgroup>
        <tbody>
          {block.rows.map((row, rowIndex) => {
            const minHeight = getPreviewRowMinHeight(block, rowIndex);
            return (
              <tr key={`${index}-${rowIndex}`}>
                {row.cells.map((cell, cellIndex) => (
                  <td
                    key={cell.id || `${index}-${rowIndex}-${cellIndex}`}
                    rowSpan={cell.rowSpan && cell.rowSpan > 1 ? cell.rowSpan : undefined}
                    colSpan={cell.colSpan && cell.colSpan > 1 ? cell.colSpan : undefined}
                    style={{
                      textAlign: cell.style?.textAlign as any,
                      verticalAlign: toCssVerticalAlign(cell.style?.verticalAlign),
                      background: cell.style?.background,
                      fontSize: `${cell.style?.fontSize}pt`,
                      fontWeight: cell.style?.bold ? 700 : 400,
                      fontStyle: cell.style?.italic ? 'italic' : undefined,
                      padding: `${paddingY}pt ${paddingX}pt`,
                      height: minHeight ? `${minHeight}pt` : undefined,
                    }}
                  >
                    {renderPreviewCell(cell)}
                  </td>
                ))}
              </tr>
            );
          })}
        </tbody>
      </table>
    );
  }

  function renderPreviewCell(cell: UoWordTableCell) {
    const imageStyle = { width: `${PREVIEW_SIGNATURE_MAX_WIDTH}pt` };
    if (cell.editor === 'signature') {
      const signature = getSignatureValue(cell);
      return (
        <>
          {cell.lockedLabel && <div>{cell.lockedLabel}</div>}
          {signature && <img className='uo-preview-signature-image' style={imageStyle} src={signature} alt='Semnatura' />}
        </>
      );
    }

    if (cell.text?.startsWith('data:image')) {
      return <img className='uo-preview-signature-image' style={imageStyle} src={cell.text} alt='Semnatura' />;
    }

    return <span>{cell.text}</span>;
  }

  function renderPreviewAttachments(files: UploadFile[]) {
    if (!files?.length) {
      return null;
    }

    return (
      <section className='uo-word-section'>
        <div className='uo-section-title'>Atasamente</div>
        {files.map((file) => (
          <div key={file.uid || file.name}>{file.name}</div>
        ))}
      </section>
    );
  }

  function renderPreviewSignature(label: string, signature?: string) {
    return (
      <section className='uo-preview-signature'>
        <strong>{label}</strong>
        {signature ? <img className='uo-signature-preview' src={signature} alt={label} /> : <span>-</span>}
      </section>
    );
  }

  return (
    <div className='uo-rapoarte'>
      {renderEditor()}
      <Modal
        title={previewRecord ? `${previewMode === 'word' ? 'Previzualizare Word' : 'Previzualizare PDF'} - ${previewRecord.number || previewRecord.payload?.templateLabel || 'document'}` : ''}
        open={!!previewRecord}
        onCancel={closePreview}
        width={previewMode === 'pdf' ? 1180 : 1040}
        footer={
          previewRecord ? (
            <Space wrap>
              <Button icon={<EyeOutlined />} onClick={() => previewWordReport(previewRecord)}>
                Preview Word
              </Button>
              <Button icon={<EyeOutlined />} onClick={() => previewPdfReport(previewRecord)}>
                Preview PDF
              </Button>
              <Button icon={<DownloadOutlined />} onClick={() => exportReport(previewRecord, 'docx')}>
                Descarca DOCX
              </Button>
              <Button icon={<DownloadOutlined />} onClick={() => exportReport(previewRecord, 'pdf')}>
                Descarca PDF
              </Button>
              <Button onClick={closePreview}>Inchide</Button>
            </Space>
          ) : null
        }
      >
        {renderPreview()}
      </Modal>
    </div>
  );
};

export default UORapoarte;
